import { isArray, pureStructure } from '@cutwise/utils';
import { queryAPI, storageAPI } from '@cutwise/utils';

import { HttpStatus } from '@/dmdrive/core/constants/network';
import { KEY_TOKEN } from '@/dmdrive/core/constants/storage';
import { UNAUTHORIZED_ERROR_MESSAGE } from '@/dmdrive/core/entities/AppError';
import { AuthService } from '@/dmdrive/core/service/AuthService';

export type FetchApiQuery = Partial<Record<string, Any>> | string;
type FetchApiBody = FormData | unknown;

type RequestOptions = Omit<RequestInit, 'body' | 'headers'> &
  Partial<{
    body: FetchApiBody;
    query: FetchApiQuery;
    group: StringOrNumber;
    headers: Partial<Record<string, Nullable<StringOrNumber>>>;
    base: string;
    responseType: 'json' | 'text' | 'blob' | 'arrayBuffer';
    repeatRequestTimeout: number;
    repeatRequestRetryTimeout: number;
    abortController: AbortController;
  }>;

type MakeRequestOptions = RequestOptions &
  Partial<{
    query: FetchApiQuery;
    body: FetchApiBody;
  }>;

type FetchApiResponse<T = Response> = Promise<T>;

class ResponseError extends Error {
  message: string;
  status?: number;
  response?: Response;

  constructor(message: string, status?: number, response?: Response) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
  }
}

class FetchRequest<T = Response> {
  url: string;

  private readonly options: MakeRequestOptions;

  constructor(url: string, options: RequestOptions) {
    this.url = url;
    this.options = options;
  }

  make(): FetchApiResponse<T> {
    return new Promise((resolve, reject) => {
      const { repeatRequestTimeout = 0, repeatRequestRetryTimeout = 0 } = this.options ?? {};

      let requestTimeout: NodeJS.Timeout;
      let retryTimeout: NodeJS.Timeout;

      function proceedError(e: Error) {
        clearTimeout(requestTimeout);
        clearTimeout(retryTimeout);

        reject(e);
      }

      const requestWrapper = () => {
        this.fetchWrapper()
          .then((response) => resolve(response))
          .catch((e) => {
            // If request was aborted - script will stop executing and not success or error result won't be applied
            if (e.code === 20) {
              return;
            }

            if (e.status === HttpStatus.NotFound) {
              e.message = `URL "${e.response.url}" not found`;
            }

            if (!repeatRequestTimeout) {
              return proceedError(e);
            }

            retryTimeout = setTimeout(requestWrapper, repeatRequestRetryTimeout);

            // Be sure that timeout for request initialized only once
            if (!requestTimeout) {
              requestTimeout = setTimeout(() => proceedError(e), repeatRequestTimeout);
            }
          });
      };

      requestWrapper();
    });
  }

  private fetchWrapper() {
    const {
      base = process.env.DMCLOUD_API_URI,
      query,
      headers,
      body,
      responseType,
      abortController,
      ...rest
    } = this.options ?? {};

    const input = `${base}${this.url}${queryAPI.generateQuery(query)}`;

    return fetch(input, {
      method: this.options.method,
      headers: this.prepareHeaders(headers),
      body: this.prepareBody(body),
      signal: abortController?.signal,
      ...rest,
    }).then((response: Response) => {
      if (!response.ok) {
        return this.prepareError(response).then((error: ResponseError) => {
          throw error;
        });
      }

      if (response.status === 204) {
        return response;
      }

      switch (responseType) {
        case 'text':
          return response.text();
        case 'blob':
          return response.blob().then((file) => [file, response]);
        case 'arrayBuffer':
          return response.arrayBuffer();
        case 'json':
        default:
          return response.json();
      }
    });
  }

  private prepareHeaders(headers: RequestOptions['headers'] = {}): HeadersInit {
    const requestHeaders: Partial<Record<string, unknown>> = {
      Authorization: `Bearer ${storageAPI.get(KEY_TOKEN)}`,
      'Content-Type': 'application/json',
      ...headers,
    };

    if (headers['Content-Type'] === null) {
      delete requestHeaders['Content-Type'];
    }

    return requestHeaders as HeadersInit;
  }

  private prepareBody(body?: FetchApiBody): BodyInit | string | undefined {
    if (!body) {
      return undefined;
    }

    if (body instanceof FormData) {
      return body;
    }

    if (isArray(body)) {
      return JSON.stringify(body);
    }

    return JSON.stringify(pureStructure(body));
  }

  private prepareError(response: Response): Promise<ResponseError> {
    if (response.status === HttpStatus.Unauthorized) {
      AuthService.redirectToLogin();

      return Promise.resolve(new ResponseError(UNAUTHORIZED_ERROR_MESSAGE, response.status, response));
    }

    if (response?.text) {
      return response.text().then((error) => {
        const status = response.status;
        let message = response.statusText;

        try {
          message = JSON.parse(error)?.message ?? message;
        } catch (e) {
          //
        }

        return new ResponseError(message, status, response);
      });
    }

    if (response?.json) {
      return response.json().then((error) => new ResponseError(response.statusText, response.status, error));
    }

    return Promise.resolve(new ResponseError(response.statusText, response.status, response));
  }
}

class FetchAPI {
  private readonly requests: FetchRequest<unknown>[] = [];

  get<T>(url: string, query: FetchApiQuery = {}, options: RequestOptions = {}) {
    return this.makeRequest<T>(url, 'GET', {
      ...options,
      query,
    });
  }

  post<T = Response>(url: string, options: RequestOptions = {}) {
    return this.makeRequest<T>(url, 'POST', options);
  }

  put<T>(url: string, options: RequestOptions = {}) {
    return this.makeRequest<T>(url, 'PUT', options);
  }

  remove<T>(url: string, options?: RequestOptions) {
    return this.makeRequest<T>(url, 'DELETE', options);
  }

  patch<T>(url: string, options?: RequestOptions) {
    return this.makeRequest<T>(url, 'PATCH', options);
  }

  private makeRequest<T>(url: string, method: string, options?: MakeRequestOptions): FetchApiResponse<T> {
    const request = new FetchRequest<T>(url, {
      method,
      ...options,
    });

    this.requests.push(request);

    return request.make().finally(() => {
      const requestIndex = this.requests.findIndex((requestElement) => requestElement === request);

      if (requestIndex !== -1) {
        this.requests.splice(requestIndex, 1);
      }
    });
  }
}

export const FetchService = new FetchAPI();
export { ResponseError };
export type { FetchApiResponse, RequestOptions };
